// src/theme.js
// Filepath: // src/theme.js
import { createTheme } from "@mui/material/styles";

const theme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#F9102C" },
    secondary: { main: "#ffcc00" },
    background: { default: "#1A0A28", paper: "#1a1a22" },
    text: { primary: "#e7e7f0", secondary: "#a0a0b3" },
    divider: "rgba(255,255,255,0.08)",
    focus: { main: "#ffcc00" },
  },
  shape: { borderRadius: 1 },
  typography: {
    fontFamily:
      'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    h1: { fontSize: "2.2rem", fontWeight: 800 },
    h2: { fontSize: "1.6rem", fontWeight: 700 },
    h3: { fontSize: "1.1rem", fontWeight: 600 },
    h4: { fontSize: "1.3rem", fontWeight: "normal" },
    button: { fontWeight: 700, textTransform: "none", letterSpacing: 0.2 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          overscrollBehavior: "none",
          userSelect: "none",
          WebkitTapHighlightColor: "transparent",
        },

        /* REMOVE the global yellow outline for non-button focusables */
        ".focused:not(.MuiButtonBase-root):not(:has(.MuiButtonBase-root))": {
          outline: "none",
        },

        /* Make doubly sure posters never show an outline */
        ".poster-root.focused, .poster-root:focus": {
          outline: "none !important",
        },

        /* If a wrapper has .focused and contains a MUI Button, don't draw any outline… */
        ".focused:has(.MuiButton-root)": {
          outline: "none",
        },
        /* …and style the child button as focused */
        ".focused:has(.MuiButton-root) .MuiButton-root": {
          backgroundColor: "#F9102C",
          color: "#ffffff",
          border: "3px solid white",
          boxShadow: "none",
        },
      },
    },

    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
    MuiCard: { styleOverrides: { root: { boxShadow: "0 6px 24px rgba(0,0,0,0.25)" } } },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: "10px",
          "&.focused": { backgroundColor: "rgba(255, 204, 0, 0.15)" },
        },
      },
    },

    /* Global Button look — fewer per-element sx */
    MuiButton: {
      defaultProps: { disableElevation: true, disableRipple: true },
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: theme.typography.h4.fontSize,
          fontWeight: theme.typography.h3.fontWeight,
          textTransform: "none",
          borderRadius: 999,
          padding: "10px 24px",
          color: "#ffffff",
          backgroundColor: "#000000",
          border: "3px solid #ffffff",
          boxShadow: "none",
          "&:hover": {
            backgroundColor: "#000000",
            border: "3px solid #ffffff",
            boxShadow: "none",
          },
          "&:focus, &:focus-visible": {
            outline: "none !important",
            boxShadow: "none",
          },
          "&.Mui-focusVisible, &.focused": {
            backgroundColor: theme.palette.primary.main,
            color: "#ffffff",
            border: "3px solid transparent",
            boxShadow: "none",
          },
          "&.Mui-disabled": {
            opacity: 0.5,
            color: "rgba(255,255,255,0.6)",
            borderColor: "rgba(255,255,255,0.3)",
            backgroundColor: "#000000",
          },
        }),
        contained: {
          backgroundColor: "#000000",
          border: "3px solid #ffffff",
          boxShadow: "none",
          "&:hover": { backgroundColor: "#000000", boxShadow: "none" },
        },
        outlined: {
          borderWidth: 2,
          borderColor: "#ffffff",
          color: "#ffffff",
          boxShadow: "none",
          "&:hover": { borderColor: "#ffffff", boxShadow: "none" },
        },
        text: {
          backgroundColor: "#000000",
          border: "3px solid #ffffff",
          boxShadow: "none",
          "&:hover": { backgroundColor: "#000000", boxShadow: "none" },
        },
        sizeSmall: { padding: "8px 16px" },
        sizeLarge: { padding: "12px 24px" },
      },
    },

    /* Text fields: rounded pills and better inner padding */
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          "& .MuiOutlinedInput-notchedOutline": {
            borderWidth: 2,
            borderColor: "rgba(255,255,255,0.6)",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#ffffff",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#ffffff",
          },
        },
        /* this is the important part for moving the text inward */
        input: {
          paddingTop: 18,
          paddingBottom: 18,
          paddingLeft: 30,   // add horizontal space inside the pill
          paddingRight: 30,  // keeps symmetry and helps with long text
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: "outlined" },
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": { borderRadius: 999 },
        },
      },
    },

    /* Links under the button should be white by default */
    MuiLink: {
      defaultProps: { underline: "hover", color: "inherit" },
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.common.white,
          "&:hover": { color: theme.palette.common.white, opacity: 0.9 },
        }),
      },
    },
  },
});

export default theme;
